import { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { usePlatformPrivilege, usePrivilege } from '@mews-commander/core';
import { GeneralEnterprise, useAppConfiguration } from '@mews-commander/data-access';
import { DataTestId, FeatureFlag, TextKey } from '@mews-commander/enums';
import { IconName } from '@optimus-web/core';
import { LocalizedText, useFeatureFlag } from '@mews/framework';
import { PlatformEnvironment } from '@mews/enums';
import { getCurrentEnterprise } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { hasCurrentEnterpriseServiceProviderMode } from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import {
    isCurrentProfilePlatformAdmin,
    hasCurrentProfileAnyServicePrivileges,
} from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { createListItemWithLink } from '../helpers';
import { SidebarExpandableListItem, SidebarFontIcon } from '../style';

export const SettingsMenu = ({ ...other }: $TSFixMe) => {
    const isPlatformAdmin = useSelector(isCurrentProfilePlatformAdmin);
    const isProduction = useAppConfiguration().environment === PlatformEnvironment.Production;
    const currentEnterprise: GeneralEnterprise = useSelector(getCurrentEnterprise);
    const isEmployeeManager = usePrivilege('manageEmployees');
    const isPlatformProfilesManager = usePlatformPrivilege('managePlatformProfiles');
    const hasServiceProviderMode = useSelector(hasCurrentEnterpriseServiceProviderMode);
    const canConfigureEnterprise = usePrivilege('configureEnterprise');
    const canConfigureService = useSelector(hasCurrentProfileAnyServicePrivileges);
    const canConfigureMailTemplates = usePrivilege('manageMailTemplates');
    const canConfigureGuestJourney = usePrivilege('manageServiceConfigurations');
    const canManagePolicies = usePrivilege('managePaymentAndCancellationPolicies');
    const manageMailTemplatesEnabled = useFeatureFlag(FeatureFlag.manageMailTemplatesPermissionEnabled);
    const whitelistedUserEmailsFeatureEnabled = useFeatureFlag(FeatureFlag.whitelistedUserEmails);

    const enterpriseId = currentEnterprise.id;
    const showEmployeeProfilesLink = isEmployeeManager && !isPlatformAdmin;
    const showPropertySettingsLink =
        canConfigureEnterprise || canConfigureGuestJourney || (manageMailTemplatesEnabled && canConfigureMailTemplates);

    return (
        <SidebarExpandableListItem
            data-test-id={DataTestId.SettingsMenu}
            leftIcon={<SidebarFontIcon value={IconName.Settings} />}
            primaryText={<LocalizedText textKey={TextKey.Settings} />}
            {...other}
        >
            {showEmployeeProfilesLink
                ? createListItemWithLink({
                      textKey: TextKey.UserPlural,
                      iconName: IconName.Employees,
                      path: 'UserManagement/PropertyUsers/Index',
                  })
                : null}
            {isPlatformProfilesManager &&
                createListItemWithLink({
                    textKey: TextKey.PlatformProfilePlural,
                    iconName: IconName.Employees,
                    path: 'PlatformProfile/Index',
                })}
            {isPlatformAdmin &&
                createListItemWithLink({
                    textKey: TextKey.PortfolioPlural,
                    iconName: IconName.Property,
                    path: 'Portfolio/Index',
                })}
            {isPlatformAdmin &&
                createListItemWithLink({
                    textKey: TextKey.ChainPlural,
                    iconName: IconName.Property,
                    path: 'Chain/Index',
                })}
            {enterpriseId && (
                <Fragment>
                    {showPropertySettingsLink
                        ? createListItemWithLink({
                              textKey: TextKey.Property,
                              iconName: IconName.Property,
                              path: 'Enterprise/Detail/$enterpriseId',
                              params: { enterpriseId },
                          })
                        : null}
                    {hasServiceProviderMode && canConfigureService
                        ? createListItemWithLink({
                              textKey: TextKey.ServicePlural,
                              iconName: IconName.Items,
                              path: 'Service/Index',
                              search: { OwnerId: enterpriseId },
                          })
                        : null}
                </Fragment>
            )}
            {canManagePolicies &&
                createListItemWithLink({
                    textKey: TextKey.GeneralPolicyPlural,
                    iconName: IconName.GatewayPayments,
                    path: 'Policies/Index',
                })}
            {!isProduction &&
                whitelistedUserEmailsFeatureEnabled &&
                isPlatformAdmin &&
                createListItemWithLink({
                    textKey: TextKey.WhitelistedUserEmailsEnterprisesMenuItem,
                    iconName: IconName.EmailQueues,
                    path: 'GuestExperience/Enterprises',
                })}
        </SidebarExpandableListItem>
    );
};
